import { useTranslation } from 'react-i18next';
import { Text } from '@kognitos/lattice';

import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import AutomationIntegrationsPanel from '@/modules/automation/components/AutomationIntegrationsPanel/AutomationIntegrationsPanel';

import { DraftRailSection } from './DraftRailSection';
import { RailAddIntegrationButton } from './RailAddIntegrationButton';
import { RailIntegrationCard } from './RailIntegrationCard';
import type { IRailIntegrations } from './use-rail-integrations';

interface IRailIntegrationsSectionProps {
  automationId: string;
  integrations: IRailIntegrations;
  /** When set, "+" opens the integration picker instead of the used-integrations popover. */
  onAddIntegration?: (integration: Bdkv2BookDescriptor) => void;
}

/** The rail's Integrations block: one card per connection the draft uses. */
export function RailIntegrationsSection({
  automationId,
  integrations,
  onAddIntegration,
}: IRailIntegrationsSectionProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { connections, connectionDescriptorById } = integrations;

  return (
    <DraftRailSection
      title={tAutomation('automation.draftRail.integrations')}
      name="integrations"
      count={connections.length}
      action={
        onAddIntegration ? (
          <RailAddIntegrationButton onAddIntegration={onAddIntegration} />
        ) : (
          <AutomationIntegrationsPanel
            automationId={automationId}
            isDraft
            triggerVariant="railIcon"
          />
        )
      }
    >
      {connections.length === 0 && (
        <Text level="xSmall" color="muted" className="px-1 py-2">
          {tAutomation('automation.draftRail.noIntegrations')}
        </Text>
      )}
      {connections.map((connection, index) => (
        <RailIntegrationCard
          key={
            connection.connection_alias ||
            connection.connection_id ||
            `${connection.book}-${index}`
          }
          connection={connection}
          descriptor={
            connection.connection_id
              ? connectionDescriptorById.get(connection.connection_id)
              : undefined
          }
        />
      ))}
    </DraftRailSection>
  );
}
